import { Injectable } from '@angular/core';
import { LikelihoodColorLevel, OelCorrectionParams, RiskRegisterAgentEntry, RiskRegisterControl } from '../../../../models/risk-assessment.model';
import { backgroundCssStyle, sanitizeHexColor } from '../../../../shared/utils/css-color.util';
import { OelCorrectionMethod, OelCorrectionMethodLabels } from '../../../../enums/oelCorrectionMethod.enum';
import { ControlStatusEnum } from '../../../../enums/control-status.enum';
import { ControlTypeEnum } from '../../../../enums/control-type.enum';
import { ComplianceStateDisplayMap, ComplianceStateEnum } from '../../../../enums/complianceState.enum';
import { RiskRegisterControlLinkType } from '../../../../enums/riskRegisterControlLinkType.enum';
import { ShiftType } from '../../../../enums/shiftTypeValue.enum';
import { RiskRegisterAssessmentStatusEnum } from '../../../../enums/assessmentStatus.enum';
import { getRegisterStatisticValue } from '../../../../shared/utils/risk-statistic-value.util';
import { RR_STYLES } from '../risk-register-table-widget/risk-register-table-presentation.service';
import {
    RiskRegisterSegGroup,
    SegAgentRow,
    SegControlRow,
    SegJobRoleView,
    SegProfile,
    SegTaskGroupView
} from '../../../../services/template-mappers/entities/risk-register-seg-group.model';

/**
 * Inline styles for every SEG Profile widget. Colour and layout MUST be inline (not component SCSS)
 * because the server-side Puppeteer PDF path does not carry external stylesheets — the same reason
 * DASH-2387's matrix widget uses RR_STYLES. We reuse the matrix base styles and add only the pieces
 * the SEG sections need (definition grid, section headings, key/value rows).
 */
export const SEG_STYLES = {
    section: 'margin-bottom:14px;page-break-inside:avoid;break-inside:avoid;',
    heading: 'font-size:13px;font-weight:bold;color:#000;margin:0 0 6px 0;font-family:Arial,sans-serif;',
    subHeading: 'font-size:11px;font-weight:bold;color:#333;margin:8px 0 4px 0;font-family:Arial,sans-serif;',
    description: 'font-size:11px;color:#333;margin:0 0 8px 0;font-family:Arial,sans-serif;',
    defGrid: 'display:flex;flex-wrap:wrap;gap:4px 24px;font-size:11px;color:#000;font-family:Arial,sans-serif;',
    defItem: 'display:inline-flex;gap:4px;',
    defLabel: 'font-weight:bold;',
    table: RR_STYLES.table,
    headerCell: RR_STYLES.headerCell,
    fixedCell: RR_STYLES.fixedCell,
    cell: `${RR_STYLES.fixedCell.replace('white-space:nowrap;', '')}white-space:normal;`,
    bandCell: RR_STYLES.matrixCell,
    tag: 'font-size:10px;color:#333;',
    noData: 'font-size:11px;color:#777;font-style:italic;'
} as const;

export interface SegProfileLabels {
    adequate: string;
    notAdequate: string;
    notAvailable: string;
    exposureLikelihoodLevels: LikelihoodColorLevel[];
}

@Injectable({ providedIn: 'root' })
export class SegProfilePresentationService {
    /** Projects the SEG-grouped source rows into display-ready SEG profiles (ratings as labels). */
    buildProfiles(groups: RiskRegisterSegGroup[] | null | undefined, labels: SegProfileLabels): SegProfile[] {
        if (!groups?.length) return [];
        return groups.map(group => ({
            segId: group.segId,
            segName: group.segName ?? '',
            segDescription: group.segDescription ?? '',
            segCode: group.segCode ?? '',
            numberOfWorkers: group.numberOfWorkers ?? 0,
            numberOfContractors: group.numberOfContractors ?? 0,
            shiftType: this.resolveShiftType(group.shiftType),
            shiftLengthHours: this.num(group.shiftLengthHours),
            avgWeeklyHours: this.num(group.avgWeeklyHours),
            jobRoles: this.buildJobRoles(group, labels),
            agents: (group.registers ?? []).map(entry => this.buildAgentRow(entry, labels)),
            controls: (group.controls ?? []).map(control => this.buildControlRow(control, labels)),
            taskGroups: this.buildTaskGroups(group)
        }));
    }

    private buildJobRoles(group: RiskRegisterSegGroup, labels: SegProfileLabels): SegJobRoleView[] {
        // BE `SegJobRollupDto` exposes the job classification as `jobName` (not `roleName`); the view
        // model keeps `roleName` as its display field so the widget/loop-template bindings are stable.
        return (group.jobRoles ?? []).map(role => ({
            roleName: role.jobName?.trim() || labels.notAvailable,
            sampleCount: role.sampleCount != null ? String(role.sampleCount) : '0'
        }));
    }

    /** Resolves the BE numeric `ShiftType` ordinal to its label (e.g. 2 -> "4x4"); '-' when unset/unknown. */
    private resolveShiftType(value: number | null | undefined): string {
        if (value == null) return '-';
        const name = ShiftType[Number(value)];
        return typeof name === 'string' ? name.replace('Shift_', '') : '-';
    }

    private buildTaskGroups(group: RiskRegisterSegGroup): SegTaskGroupView[] {
        // BE `SegTaskGroupDto` fields are `name` and `tasks[].name` (not `groupName`/`taskName`).
        return (group.taskGroups ?? []).map(taskGroup => ({
            // Merged DASH-2700 BE serialises SegTaskGroupDto/SegTaskDto as { name } (camelCase), not groupName/taskName.
            groupName: taskGroup.name?.trim() || '',
            tasks: (taskGroup.tasks ?? [])
                .map(task => (typeof task === 'string' ? task : task?.name ?? ''))
                .filter(name => !!name)
        }));
    }

    private buildControlRow(control: RiskRegisterControl, labels: SegProfileLabels): SegControlRow {
        return {
            controlCode: control.controlCode ?? '-',
            controlName: control.controlName ?? '-',
            controlType: this.enumName(ControlTypeEnum, control.controlType, labels.notAvailable),
            controlBucketName: control.controlBucketName ?? '-',
            controlOwnerName: control.controlOwnerName ?? '-',
            status: this.enumName(ControlStatusEnum, control.status, labels.notAvailable),
            targetDates: this.dateRange(control.targetImplementationStart, control.targetImplementationEnd),
            actualDates: this.dateRange(control.implementationStartDate, control.implementationEndDate),
            linkType: this.enumName(RiskRegisterControlLinkType, control.linkType, labels.notAvailable)
        };
    }

    private buildAgentRow(entry: RiskRegisterAgentEntry, labels: SegProfileLabels): SegAgentRow {
        const levels = labels.exposureLikelihoodLevels;
        const na = labels.notAvailable;
        return {
            agentName: (entry.agentName as string) ?? '',
            exposureBand: getRegisterStatisticValue(entry, 'exposureBand', levels, na),
            likelihood: getRegisterStatisticValue(entry, 'likelihood', levels, na),
            methodOfAssessment: getRegisterStatisticValue(entry, 'methodOfAssessment', levels, na),
            baseOel: this.num(entry.oel),
            correctedOel: this.num(entry.correctedOEL),
            correctionMethod: this.correctionMethodLabel(entry.oelCorrectionMethod, na),
            correctionFactors: this.correctionFactors(entry),
            correctionParams: this.correctionParams(entry),
            samples: `${this.int(entry.quantitativeNumberOfSamples)} / ${this.int(entry.requiredNumberOfSamples)}`,
            qualitativeScores: this.scorePair(entry.qualitativeExposureScore, entry.qualitativeFrequencyScore),
            residualScores: this.scorePair(entry.residualExposureScore, entry.residualFrequencyScore),
            controlAdequacy: this.adequacy(entry.isControlAdequate, labels),
            postTreatmentControlAdequacy: this.adequacy(entry.isPostTreatmentControlAdequate, labels),
            complianceState: this.complianceLabel(entry.complianceState, na),
            assessmentStatus: this.enumName(RiskRegisterAssessmentStatusEnum, entry.assessmentStatus, na),
            reassessmentFrequencyMonths: entry.reassessmentFrequencyMonths != null ? String(entry.reassessmentFrequencyMonths) : '-',
            note: (entry.note as string)?.trim() || '-',
            residualNotes: (entry.residualNotes as string)?.trim() || '-',
            cellStyle: backgroundCssStyle(sanitizeHexColor(entry.color))
        };
    }

    private correctionMethodLabel(value: unknown, notAvailable: string): string {
        if (value == null) return notAvailable;
        const label = OelCorrectionMethodLabels[value as OelCorrectionMethod];
        return label ?? notAvailable;
    }

    private correctionFactors(entry: RiskRegisterAgentEntry): string {
        const total = entry.totalOelCorrectionFactor;
        return total != null ? `×${this.num(total)}` : '-';
    }

    private correctionParams(entry: RiskRegisterAgentEntry): string {
        const params = this.parseCorrectionParams(entry.oelCorrectionParams);
        if (!params) return '-';
        const parts: string[] = [];
        if (params.shiftLengthHours != null) parts.push(`${this.num(params.shiftLengthHours)}h`);
        if (params.weeklyHours != null) parts.push(`${this.num(params.weeklyHours)}h/wk`);
        if (params.pressureMmHg != null) parts.push(`${this.num(params.pressureMmHg)}`);
        return parts.length ? parts.join(', ') : '-';
    }

    /**
     * BE `RiskRegisterDto.OelCorrectionParams` is a JSON *string* column (DASH-1364), serialised camelCase
     * (JsonNamingPolicy.CamelCase of OelCorrectionParameters.cs) so the real wire keys are
     * `shiftLengthHours`/`weeklyHours`/`pressureMmHg`. Parse then map onto the OelCorrectionParams wire
     * shape — same pattern as risk-register-details-popup.component.ts. The legacy `shiftLength`/`pressure`
     * keys are still accepted as a defensive fallback for any older persisted rows. Tolerates an
     * already-parsed object and never throws on malformed JSON.
     */
    private parseCorrectionParams(raw: unknown): OelCorrectionParams | null {
        if (raw == null || raw === '') return null;
        try {
            const p = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown>;
            const pick = (...keys: string[]): number | undefined => {
                for (const k of keys) {
                    const v = p[k];
                    if (v != null) return v as number;
                }
                return undefined;
            };
            return {
                shiftLengthHours: pick('shiftLengthHours', 'shiftLength'),
                weeklyHours: pick('weeklyHours'),
                pressureMmHg: pick('pressureMmHg', 'pressure')
            };
        } catch {
            return null;
        }
    }

    private complianceLabel(value: unknown, notAvailable: string): string {
        if (value == null) return notAvailable;
        return ComplianceStateDisplayMap[value as ComplianceStateEnum]?.label ?? notAvailable;
    }

    private adequacy(value: unknown, labels: SegProfileLabels): string {
        if (value == null) return labels.notAvailable;
        return value ? labels.adequate : labels.notAdequate;
    }

    private scorePair(exposure: unknown, frequency: unknown): string {
        const e = exposure != null ? String(exposure) : '-';
        const f = frequency != null ? String(frequency) : '-';
        return `${e} × ${f}`;
    }

    private dateRange(start: unknown, end: unknown): string {
        const s = start ? String(start) : '';
        const e = end ? String(end) : '';
        if (!s && !e) return '-';
        return `${s || '-'} → ${e || '-'}`;
    }

    private enumName(enumObj: Record<string, unknown>, value: unknown, notAvailable: string): string {
        if (value == null) return notAvailable;
        const name = enumObj[value as number];
        return typeof name === 'string' ? name : notAvailable;
    }

    private num(value: unknown): string {
        if (value == null || value === '') return '-';
        const n = Number(value);
        return Number.isFinite(n) ? parseFloat(n.toFixed(2)).toString() : '-';
    }

    private int(value: unknown): string {
        if (value == null) return '0';
        const n = Number(value);
        return Number.isFinite(n) ? String(Math.trunc(n)) : '0';
    }
}
